/** @format */

import { isPlatformBrowser } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID, makeStateKey, TransferState } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';

import { HttpService } from '@shared/data-access/services';
import { CustomHttpHeaders, MediaType } from '@shared/data-access/enums';
import { Page } from '../interfaces/page.interface';
import { Chain, Environment, Breadcrumb, BreadCrumbResponse } from '@shared/data-access/interfaces';

@Injectable({
	providedIn: 'root'
})
export class SeoDao {
	readonly QUESTIONS_AND_ANSWERS_STATE_KEY_PREFIX = 'q-and-a-';
	readonly REVIEWS_STATE_KEY_PREFIX = 'reviews-';
	readonly SEARCHDEX_INCLUDE_STATE_KEY_PREFIX = 'searchdex-';
	readonly SEO_TOKEN_DETAILS_STATE_KEY_PREFIX = 'details-by-seotoken-';
	readonly PAGE_SEO_STATE_KEY_PREFIX = 'page-seo-';
	readonly BREADCRUMBS_BY_CATEGORY_ID_STATE_KEY_PREFIX = 'breadcrumb-response-';

	constructor(
		private chain: Chain,
		private environment: Environment,
		@Inject(PLATFORM_ID) protected platformId: any,
		private http: HttpClient,
		private state: TransferState,
		private httpService: HttpService
	) {}

	/**
	 * @description
	 * Attempts to call an endpoint to get a SearchDex Managed Include. If the file is not available, a 404 will be returned.
	 */
	/* istanbul ignore next */
	/* ToDo Remove this Function, since its not being used anymore */
	public getSearchDexInclude$(fileName: string, retries = 0): Observable<any> {
		// Get unique key
		const STATE_KEY = makeStateKey(`${this.SEARCHDEX_INCLUDE_STATE_KEY_PREFIX}${fileName}`);

		// Attempt to get the state by key
		const stateData: any = this.state.get(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Custom HTTP Headers
		httpHeaders = httpHeaders.set(CustomHttpHeaders.X_PROXY_REQUEST, 'true');

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${this.environment.thirdParty.searchDex.gatewayUrlProxy.timeout}`
			);
		}

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders
		};

		// Make the HTTP Request
		return this.http
			.get(
				`${this.environment.thirdParty.searchDex.gatewayUrlProxy.path}${fileName}.json`,
				httpOptions
			)
			.pipe(
				map((response: any) => {
					// Set the state data
					this.state.set(STATE_KEY, response);

					// Return the raw data
					return response;
				}),
				retry(retries)
			);
	}

	/**
	 * Retrieves SEO data based on the provided SEO token.
	 *
	 * @param seoToken - The token used to identify the SEO data.
	 * @param retries - The number of retry attempts for the HTTP request (default is 0).
	 * @param useDefaultHttpTransferCache - Flag to determine whether to use the default angular's HTTP transferState cache (default is false).
	 * @returns An Observable that emits the SEO data.
	 */
	public getSEOData$(
		seoToken: string,
		retries = 0,
		useDefaultHttpTransferCache = false
	): Observable<any> {
		const requestTimerStart = Date.now().valueOf();
		let ELEMENT_KEY = makeStateKey(`seo-response-${seoToken}`);
		if (!useDefaultHttpTransferCache) {
			const element: any = this.state.get(ELEMENT_KEY, null);

			// Check to see if state exists
			if (element) {
				const executionTime = Date.now().valueOf() - requestTimerStart;
				console.log(
					`Execution time for - Cached SEO API Request for ${this.chain.storeId}, /f/${seoToken} - ${executionTime}ms`
				);

				// Return the existing state
				return new Observable((observer) => {
					observer.next(element);
					observer.complete();
				});
			}
		}
		const requestUrl = `${this.environment.api.seoByChain}seo-category/v1/categories?seoUrl=${seoToken}&storeId=${this.chain.storeId}&children=true&published=true`;
		let httpHeaders = new HttpHeaders({
			'Content-Type': MediaType.APPLICATION_JSON
		});

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		const httpOptions = {
			headers: httpHeaders
		};

		return this.http.get(requestUrl, httpOptions).pipe(
			map((response: any) => {
				const executionTime = Date.now().valueOf() - requestTimerStart;
				console.log(
					`Execution time for - SEO API Request for ${this.chain.storeId}, /f/${seoToken} - ${executionTime}ms`
				);
				if (!useDefaultHttpTransferCache) {
					this.state.set<any>(ELEMENT_KEY, response);
				}
				return response;
			}),
			retry(retries)
		);
	}

	/**
	 * @description
	 * Gets the Page Seo by Url.The returned Page object will contain SEO related information.
	 */
	public getPageSeobyUrl$(url: string, retries = 0): Observable<Page> {
		// Get unique key
		const STATE_KEY = makeStateKey(`${this.PAGE_SEO_STATE_KEY_PREFIX}${url}`);

		// Attempt to get the state by key
		const stateData: any = this.state.get(STATE_KEY, null);

		// Check to see if state exists
		if (stateData) {
			// Return the existing state
			return new Observable((observer) => {
				observer.next(stateData);
				observer.complete();
			});
		}
		// Query String Parameters
		let httpParams = new HttpParams();

		// Set channel param
		httpParams = httpParams.set('channel', `${this.chain.chainMarketingIdentifierAbbr}`);

		// set inventory param
		httpParams = httpParams.set('inventory', 'true');

		// set active param
		httpParams = httpParams.set('active', 'true');

		// set skus param
		httpParams = httpParams.set('skus', 'false');

		// Set channel param
		httpParams = httpParams.set('url', `${url}`);

		// Standard HTTP Headers
		let httpHeaders = new HttpHeaders({
			Accept: MediaType.APPLICATION_JSON
		});

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		// Check platform
		if (!isPlatformBrowser(this.platformId)) {
			// Set a timeout on the server to prevent the response from taking too long
			httpHeaders = httpHeaders.set(
				CustomHttpHeaders.X_TIMEOUT,
				`${this.environment.api.spring.microServiceProxy.seoservice.timeout}`
			);
		}

		// HTTP Options
		const httpOptions = {
			headers: httpHeaders,
			params: httpParams,
			withCredentials: true
		};

		// Make the HTTP Request
		return this.http
			.get(
				`${this.environment.api.seoByChain}catalog-productdetails/v4/byURL/${this.chain.storeId}`,
				httpOptions
			)
			.pipe(
				map((response: any) => {
					// Return page object
					let returnPage: Page = {};

					// Check to see if there is a result
					if (
						response.productsData &&
						response.productsData[0].style &&
						response.productsData[0].style.seo
					) {
						// Set the response
						returnPage = response.productsData[0].style.seo;

						returnPage = this.getUpdatedPage(returnPage);

						// Set the state
						this.state.set(STATE_KEY, returnPage as any);
					}

					// Return the raw data
					return returnPage;
				}),
				retry(retries)
			);
	}

	/**
	 * @description
	 * Gets breadcrumbs for a given category ID
	 */
	getBreadCrumbsData$(identifier: string, retries = 1): Observable<Breadcrumb[]> {
		const STATE_KEY = makeStateKey<Breadcrumb[]>(
			`${this.BREADCRUMBS_BY_CATEGORY_ID_STATE_KEY_PREFIX}${identifier}`
		);

		//Get breadcrumbs state by key
		const breadcrumbs: Breadcrumb[] | null = this.state.get(STATE_KEY, null);

		if (breadcrumbs) {
			return of(breadcrumbs);
		}

		const requestUrl = `${this.environment.api.seoByChain}seo-category/v1/categories/breadcrumb?storeId=${this.chain.storeId}&identifier=${identifier}`;
		let httpHeaders = new HttpHeaders({
			'X-TIMEOUT': '10000',
			'Content-Type': 'application/json'
		});

		// Get Origin Host Header
		const origin = this.httpService.getRequestHeaderValueAndSetStateKey('ORIGIN_HOST');
		if (origin) {
			httpHeaders = httpHeaders.set(CustomHttpHeaders.ORIGIN, origin);
		}

		const httpOptions = {
			headers: httpHeaders
		};

		return this.http.get<BreadCrumbResponse>(requestUrl, httpOptions).pipe(
			map((response) => {
				if (response && response.breadcrumbList) {
					// Set the state
					this.state.set(STATE_KEY, response.breadcrumbList);

					return response.breadcrumbList;
				}

				return null;
			}),
			retry(retries),
			catchError((error: any) => throwError(() => error))
		);
	}

	/**
	 * @description
	 * Remove Seo Poperties if its empty
	 */
	/* istanbul ignore next */
	private getUpdatedPage(returnPage) {
		// Check the page title
		if (typeof returnPage.title === 'string' && returnPage.title.trim() === '') {
			// Delete the page title
			delete returnPage.title;
		}

		// Check the page description
		if (
			typeof returnPage.metaDescription === 'string' &&
			returnPage.metaDescription.trim() === ''
		) {
			// Delete the page description
			delete returnPage.metaDescription;
		}

		// Check the page keywords
		if (typeof returnPage.metaKeyword === 'string' && returnPage.metaKeyword.trim() === '') {
			// Delete the page keywords
			delete returnPage.metaKeyword;
		}

		return returnPage;
	}
}
